/**
 * Copyright (c) 2026, CYBRA Corp. All rights reserved.
 */
import {Component, OnInit, ViewChild, ElementRef, ChangeDetectorRef} from '@angular/core';
import {ActivatedRoute} from "@angular/router";
import {EmGroup} from "../../models/em-group";
import {GroupType} from "../../models/group-type";
import {InstanceData} from "../../models/instance-data";
import {TagDefinition} from "../../models/tag-definition";
import {TagInstance} from "../../models/tag-instance";

import {TagService} from "../../services/tag.service";
import {GroupService} from "../../services/group.service";

import {AddToGroupDialogComponent} from "../add-to-group-dialog.component";
import {ConfirmDeleteDialogComponent} from "../confirm-delete-dialog.component";
import {ManageGroupsDialogComponent} from "../manage-groups-dialog.component";
import {RemoveFromGroupDialogComponent} from "../remove-from-group-dialog.component";
import {ShowGroupDialogComponent} from "../show-group-dialog.component";

import {EntityInstanceDialogComponent} from './dialogs/entity-instance-dialog.component';
import {EntityTypeDialogComponent} from "./dialogs/entity-type-dialog.component";
import {INFRA_DUMMY_FUNCTION, PAGING_ITEMS_PER_PAGE, RESULTS_PER_PAGE} from "../../app-constants";
import {NgxSpinnerService} from "ngx-spinner";
import {Observable} from "rxjs";
import {BaseInstance} from "../../models/base-instance";
import { PagedResults } from 'app/models/paged-results';
import { FunctionParameterDataUi } from './dialogs/general-function-dialog.component';
import { ERROR_DIALOG_TITLE, SimpleSuccessErrorDialogComponent } from '../simple-success-error-dialog-box/simple-success-error-dialog-box.component';
import { Function } from 'app/models/function';
import { FilterOption, FilterType, REMOVE_FILTER } from '../../models/filter-option';

declare var $: any;

@Component({
  templateUrl: './tags.component.html',
  styleUrls: ['../../../assets/css/infrastructure.css']
})
export class TagsComponent implements OnInit {

  @ViewChild('confirmDeleteDialog')
  mConfirmDeleteDialog: ConfirmDeleteDialogComponent;

  @ViewChild('showGroupDialog')
  mShowGroupDialog: ShowGroupDialogComponent;

  @ViewChild('manageGroupsDialog')
  mManageGroupsDialog: ManageGroupsDialogComponent;

  @ViewChild('addToGroupDialog')
  mAddToGroupDialogComponent: AddToGroupDialogComponent;

  @ViewChild('removeFromGroupDialog')
  mRemoveFromGroupDialogComponent: RemoveFromGroupDialogComponent;

  @ViewChild('entityTypeDialog')
  mEntityTypeDialogComponent: EntityTypeDialogComponent;

  @ViewChild('entityInstanceDialog')
  mEntityInstanceDialogComponent: EntityInstanceDialogComponent;

  @ViewChild('fileTypeUpload')
  mTypeFileUploadInput: ElementRef;

  @ViewChild('fileTagUpload')
  mTagFileUploadInput: ElementRef;

  /** Reference to the SimpleSuccessErrorDialog used to display error */
  @ViewChild('successErrorDialog') 
  simpleSuccessErrorDialogComponent: SimpleSuccessErrorDialogComponent;

  mTagDefinitions: TagDefinition[] = [];
  mTagDefinitionsSelected: TagDefinition[] = [];
  mTagInstances: TagInstance[] = [];
  mTagInstancesSelected: TagInstance[] = [];

  mDeleteFlag = 0; //1 = Tag Definitions, 2 = Tag Instances


  //Entity Type Dialog Variables
  mEntity: TagDefinition;
  mEntityName: string;
  mEntityType = GroupType.TAG;
  mEntityTypeName = "Tag";


  //Entity Instance Dialog Variables
  mInstance: TagInstance;
  mInstanceData: InstanceData[];
  mInstanceName: string;
  mInstanceEntityType: TagDefinition;
  mInstanceType = GroupType.TAG;
  mInstanceTypeName = "Tag";
  mAvailableEntityTypes: TagDefinition[];

  // Variables for paginator
  //pageSize = PAGING_ITEMS_PER_PAGE;
  numberTags = 0;
  numberTagPages = 0;
  numberTagInstances = 0;
  numberTagInstancePages = 0;
  currentPageTag = 1;
  currentPageTagInstance = 1;

  // Sorting variables
  // sortColumn = null;
  // sortOrder = {};


  // Filter Options for Tag Definitions (types)
  filterOptionsForTagDefinition: FilterOption[] = [
    REMOVE_FILTER,
    new FilterOption({ column: "name", label: "Name", type: FilterType.STRING }),
  ];
  selectedFilterOptionForTagDefinition: FilterOption = REMOVE_FILTER;

  // Filter Options for Tag Instances. "Is Deactivated" is a generic BOOLEAN
  // filter now, replacing the old hand-coded boolean handling.
  filterOptionsForTagInstance: FilterOption[] = [
    REMOVE_FILTER,
    new FilterOption({ column: "name", label: "Name", type: FilterType.STRING }),
    new FilterOption({ column: "isDeactivated", label: "Is Deactivated", type: FilterType.BOOLEAN }),
  ];
  // Tag Instances support multiple simultaneous filters (Name + Is Deactivated),
  // AND'd together, so the active filters are tracked as a list.
  activeFiltersForTagInstance: FilterOption[] = [];
  
  typeaheadLoading: boolean;
  entities: Observable<BaseInstance[]>;

  resultsPerPageValues = RESULTS_PER_PAGE;
  selectedPageSizeValueForTypes: number = this.resultsPerPageValues[0];
  selectedPageSizeValueForInstances: number = this.resultsPerPageValues[0];

  // Variables for the dropdown menu 
  functionList = [];
  selectedFunction = [];
  showContextMenu: boolean = false;
  contextMenuPosition = { x: 0, y: 0 };

  // Variable for General Confirm/Cancel Dialog Box
  showGeneralDialog: boolean = false;
  functionParameterData: FunctionParameterDataUi[] = [];

  constructor(
    private mRoute: ActivatedRoute,
    private mTagService: TagService,
    private mGroupService: GroupService,
    private spinner: NgxSpinnerService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    // this.sortOrder['name'] = "ASC";
    // this.sortOrder['instance_name'] = "ASC";
    // this.sortOrder['instance_asset'] = "ASC";

    this.mRoute.data.subscribe((data: { tagDefinitions: PagedResults<TagDefinition>, tagInstances: PagedResults<TagInstance>}) => {
      if (data.tagInstances && data.tagDefinitions) {
        this.setTagDefinitions(data.tagDefinitions.data);
        //this.loadEntityTypeUntilMatch();
        this.numberTags = data.tagDefinitions.size;
        this.numberTagPages = (this.numberTags / this.selectedPageSizeValueForTypes);

        this.setTagInstances(data.tagInstances.data);
        this.numberTagInstances = data.tagInstances.size;
        this.numberTagInstancePages = (this.numberTagInstances / this.selectedPageSizeValueForInstances);
        console.log(`number tag instance pages: ${this.numberTagInstancePages}`)
      }
    });

    //Subscribe to changes on the Filter Name Condition's Property Value to update the Type Ahead Values
    this.entities = Observable.create((observer: any) => {
      observer.next(this.selectedFilterOptionForTagDefinition.apiValue);
    }).mergeMap((token: string) => this.search(token));

  }

  async search(token: string): Promise<PagedResults<TagDefinition>>  {
    console.log("search called with token [" + token + "]");
    return await this.mTagService.getAllTagsForPaging((this.currentPageTag - 1) * this.selectedPageSizeValueForTypes, this.selectedPageSizeValueForTypes, "ASC", this.selectedFilterOptionForTagDefinition.apiValue);
  }

  changeTypeaheadLoading(e: boolean): void {
    this.typeaheadLoading = e;
  }

  private setTagDefinitions(tagDefinitions: TagDefinition[]): void {
    const objects: TagDefinition[] = [];
    if (tagDefinitions != null) {
      this.mAvailableEntityTypes = tagDefinitions;
      tagDefinitions.forEach(tagDefinition => {
        objects.push(tagDefinition);
      });
    }
    this.mTagDefinitions = objects;
  }

  private setTagInstances(tagInstances: TagInstance[]): void {
    const instances: TagInstance[] = [];
    if (tagInstances != null) {
      tagInstances.forEach(tagInstance => {
        instances.push(tagInstance);
      });
    }
    this.mTagInstances = instances;
  }

  isTagDefinitionSelected(tag: TagDefinition): boolean {
    return (this.mTagDefinitionsSelected.filter(i => i.id == tag.id).length >= 1);
  }

  isTagInstanceSelected(tag: TagInstance): boolean {
    return (this.mTagInstancesSelected.filter(i => i.id == tag.id).length >= 1);
  }

  selectTagDefinition(tag: TagDefinition): void {
    console.log(this.mTagDefinitionsSelected);
    const index = this.mTagDefinitionsSelected.indexOf(tag);
    if (index > -1) {
      console.log('Removing ' + tag.id);
      this.mTagDefinitionsSelected.splice(index, 1);
    } else {
      console.log('Adding ' + tag.id);
      this.mTagDefinitionsSelected.push(tag);
    }
  }

  selectTagInstance(tag: TagInstance): void {
    console.log(this.mTagInstancesSelected);
    const index = this.mTagInstancesSelected.indexOf(tag);
    if (index > -1) {
      console.log('Removing ' + tag.id);
      this.mTagInstancesSelected.splice(index, 1);
    } else {
      console.log('Adding ' + tag.id);
      this.mTagInstancesSelected.push(tag);
    }
  }

  isSingleTagDefinitionSelected(): boolean {
    return (this.mTagDefinitionsSelected != null && this.mTagDefinitionsSelected.length == 1);
  }

  isAnyTagDefinitionsSelected(): boolean {
    return (this.mTagDefinitionsSelected != null && this.mTagDefinitionsSelected.length > 0);
  }

  isAllTagDefinitionsSelected(): boolean {
    return (this.mTagDefinitionsSelected != null && this.mTagDefinitionsSelected.length == this.selectedPageSizeValueForTypes);
  }

  arrayRemove(array: any[], value: number): any[] {
    return array.filter(function(ele: any): any {
      return ele != value;
    });
  }

  setAllTagDefinitionsSelected(event: any): void {
    this.mTagDefinitions.forEach(tag =>
      (event.target.checked) ? this.selectTagDefinition(tag): this.unSelectTagDefinitions(tag));
  }

// setAllTagDefinitionsSelected(event: any): void {
//   this.mTagDefinitions.forEach(tag => this.mTagDefinitionsSelected = event.target.checked);
// }

  isSingleTagInstanceSelected(): boolean {
    return (this.mTagInstancesSelected != null && this.mTagInstancesSelected.length == 1);
  }

  isAnyTagInstancesSelected(): boolean {
    return (this.mTagInstancesSelected != null && this.mTagInstancesSelected.length > 0);
  }

  isAllTagInstancesSelected(): boolean {
    return (this.mTagInstancesSelected != null && this.mTagInstancesSelected.length == this.selectedPageSizeValueForInstances);
  }

  setAllTagInstancesSelected(event: any): void {
    this.mTagInstances.forEach(tag =>
      (event.target.checked) ? this.selectTagInstance(tag) : this.unSelectTagInstance(tag));
  }

  // Function to unselect the tags if the user navigates. Need to updaye  in Computer

 unSelectTagInstance(tag: TagInstance): void {
    const index = this.mTagInstancesSelected.indexOf(tag);
    if (index > -1) {
      console.log('Removing ' + tag.id);
      this.mTagInstancesSelected.splice(index, 1);
      }
  }

  unSelectTagDefinitions(tag: TagDefinition): void {
    const index = this.mTagDefinitionsSelected.indexOf(tag);
    if (index > -1) {
      console.log('Removing ' + tag.id);
      this.mTagDefinitionsSelected.splice(index, 1);
      }
  }


  getSelectedTagDefinitions(): TagDefinition[] {
    return this.mTagDefinitionsSelected;
  }

  getSelectedTagDefinitionIds(): number[] {
    const ids: number[] = [];
    this.mTagDefinitionsSelected.forEach(ui => ids.push(ui.id));
    return ids;
  }

  getSelectedTagDefinitionNames(): string[] {
    const names: string[] = [];
    this.mTagDefinitionsSelected.forEach(ui => names.push(ui.name));
    return names;
  }

  getSelectedTagInstances(): TagInstance[] {
    return this.mTagInstancesSelected;
  }

  getSelectedTagInstanceIds(): number[] {
    const ids: number[] = [];
    this.mTagInstancesSelected.forEach(ui => ids.push(ui.id));
    return ids;
  }

  getSelectedTagInstanceNames(): string[] {
    const names: string[] = [];
    this.mTagInstancesSelected.forEach(ui => names.push(ui.name));
    return names;
  }

  manageTagDefinitionGroupsClicked(): void {
    this.mManageGroupsDialog.show(GroupType.ASSET);
  }

  manageTagInstanceGroupsClicked(): void {
    this.mManageGroupsDialog.show(GroupType.TAG);
  }

  addSelectedTagDefinitionsToGroupClicked(): void {
    if (this.isAnyTagDefinitionsSelected()) {
      this.mAddToGroupDialogComponent.show(GroupType.ASSET, this.getSelectedTagDefinitionNames());
    }
  }

  addSelectedTagInstancesToGroupClicked(): void {
    if (this.isAnyTagInstancesSelected()) {
      this.mAddToGroupDialogComponent.show(GroupType.TAG, this.getSelectedTagInstanceNames());
    }
  }

  addSelectedToGroup(group: EmGroup): void {
    switch (group.type) {
      case GroupType.ASSET: {
        this
          .mGroupService
          .addItemsToGroup(group.id, this.getSelectedTagDefinitionIds())
          .then(
            success => {
              this.refreshTagDefinitions();
            },
            error => this.handleError(error)
          );
        break;
      }
      case GroupType.TAG: {
        this
          .mGroupService
          .addItemsToGroup(group.id, this.getSelectedTagInstanceIds())
          .then(
            success => {
              this.refreshTagInstances();
            },
            error => this.handleError(error)
          );
        break;
      }
    }
  }





  removeSelectedTagDefinitionsFromGroupClicked(): void {
    if (this.isAnyTagDefinitionsSelected()) {
      const groupNames: string[] = [];
      const groups: EmGroup[] = [];
      const tagDefinitionNames: string[] = [];
      this.getSelectedTagDefinitions().forEach(tagDefinition => {
        tagDefinitionNames.push(tagDefinition.name);
        if (tagDefinition.groups != null) {
          tagDefinition.groups.forEach(group => {
            if (!groupNames.includes(group.name)) {
              groupNames.push(group.name);
              groups.push(group);
            }
          });
        }
      });
      this.mRemoveFromGroupDialogComponent.show(groups, tagDefinitionNames);
    }
  }

  removeSelectedTagInstancesFromGroupClicked(): void {
    if (this.isAnyTagInstancesSelected()) {
      const groupNames: string[] = [];
      const groups: EmGroup[] = [];
      const tagInstanceNames: string[] = [];
      this.getSelectedTagInstances().forEach(tagInstance => {
        tagInstanceNames.push(tagInstance.name);
        if (tagInstance.groups != null) {
          tagInstance.groups.forEach(group => {
            if (!groupNames.includes(group.name)) {
              groupNames.push(group.name);
              groups.push(group);
            }
          });
        }
      });
      this.mRemoveFromGroupDialogComponent.show(groups, tagInstanceNames);
    }
  }

  removeSelectedFromGroup(group: EmGroup): void {
    switch (group.type) {
      case GroupType.ASSET: {
        this
          .mGroupService
          .removeItemsFromGroup(group.id, this.getSelectedTagDefinitionIds())
          .then(
            success => {
              this.refreshTagDefinitions();
            },
            error => this.handleError(error)
          );
        break;
      }
      case GroupType.TAG: {
        this
          .mGroupService
          .removeItemsFromGroup(group.id, this.getSelectedTagInstanceIds())
          .then(
            success => {
              this.refreshTagInstances();
            },
            error => this.handleError(error)
          );
        break;
      }
    }
  }





  showGroup(group: EmGroup): void {
    switch (group.type) {
      case GroupType.ASSET: {
        this
          .mGroupService
          .getAllTagDefinitionsInGroup(group.id)
          .then(
            success => {
              const itemNames: string[] = [];
              success.forEach(member => itemNames.push(member.name));
              this.mShowGroupDialog.show(group.name, itemNames);
            },
            error => this.handleError(error)
          );
        break;
      }
      case GroupType.TAG: {
        this
          .mGroupService
          .getAllTagInstancesInGroup(group.id)
          .then(
            success => {
              const itemNames: string[] = [];
              success.forEach(member => itemNames.push(member.name));
              this.mShowGroupDialog.show(group.name, itemNames);
            },
            error => this.handleError(error)
          );
        break;
      }
    }
  }

  addTagDefinitionClicked(): void {
    this.mEntity = new TagDefinition();
    this.mEntityName = null;
    this.mEntityTypeDialogComponent.mParents = this.mAvailableEntityTypes;
    this.mEntityTypeDialogComponent.entityParent = null;
    this.mEntityTypeDialogComponent.showDialog();
  }

  editSelectedTagDefinitionClicked(): void {
    if (this.isSingleTagDefinitionSelected()) {
      this.editTagDefinition(this.getSelectedTagDefinitions()[0]);
    }
  }

  duplicateSelectedTagDefinitionClicked(): void {
    if (this.isSingleTagDefinitionSelected()) {
      this
        .mTagService
        .getTagDefinitionById(this.getSelectedTagDefinitions()[0].id)
        .then(
          response => {
            response.id = null;
            if (response.properties != null) {
              response.properties.forEach(p => p.id = null);
            }
            if (response.functions != null) {
              response.functions.forEach(f => f.id = null);
            }

            this.mEntity = response;
            this.mEntityName = null;
            this.mEntityTypeDialogComponent.showDialog();
          },
          error => this.handleError(error)
        );
    }
  }

  deleteSelectedTagDefinitionsClicked(): void {
    if (this.isAnyTagDefinitionsSelected()) {
      this.mDeleteFlag = 1; //1 = Tag Definitions
      this.mConfirmDeleteDialog.show("tag type", this.getSelectedTagDefinitionNames());
    }
  }

  editTagDefinition(tagDefinition: TagDefinition): void {
    this
      .mTagService
      .getTagDefinitionById(tagDefinition.id)
      .then(
        response => {
          this.mEntity = response;
          this.mEntityName = response.name;
          this.mEntityTypeDialogComponent.entityParent = response.parent;
          this.mEntityTypeDialogComponent.mParents = this.mAvailableEntityTypes;

          if (response.parent != null) {
              this.mEntityTypeDialogComponent.entityParent = this.mAvailableEntityTypes.find(p => p.id == response.parent.id);
          }

          this.mEntityTypeDialogComponent.showDialog();
        },
        error => this.handleError(error)
      );
  }

  createOrUpdateEntityType(): void {
    if (this.mEntity.id == null) {
      //ID = null, this is a new Tag Definition... Create it
      this
        .mTagService
        .createTagDefinition(this.mEntity)
        .then(
          response => {
            this.refreshTagDefinitions();
            this.mAvailableEntityTypes.push(response);
            this.mEntityTypeDialogComponent.hideDialog();
          },
          error => this.handleError(error)
        );
    } else {
      //ID <> null, this is an existing Tag Definition... Update it
      this
        .mTagService
        .updateTagDefinition(this.mEntity)
        .then(
          response => {
            this.refreshTagDefinitions();
            this.mEntityTypeDialogComponent.hideDialog();
          },
          error => this.handleError(error)
        );
    }
  }





  addTagInstanceClicked(): void {
    //Create a new Instance to be modeled in the Entity Instance Dialog
    this.mInstance = new TagInstance();
    this.mInstanceName = null;

    //Refresh the Availabe Types, Reload the Instance Data and Show the Dialog to start editing
    this.mEntityInstanceDialogComponent.setOriginalInstanceData([]);
    this.refreshTypesReloadDataAndShowDialog();
  }

  editSelectedTagInstanceClicked(): void {
    if (this.isSingleTagInstanceSelected()) {
      this.editTagInstance(this.getSelectedTagInstances()[0]);
    }
  }

  duplicateSelectedTagInstanceClicked(): void {
    if (this.isSingleTagInstanceSelected()) {
      this
      .mTagService
      .getTagInstanceById(this.getSelectedTagInstances()[0].id)
      .then(
        response => {
          //Set the Instance to be modeled in the Entity Instance Dialog to the Instance returned...
          this.mInstance = response;

          //Set the Instance Name value so that it gets properly displayed in the Dialog Header
          this.mInstanceName = response.name;

          //Reset the IDs of the Instance and all the Instance Data so that it gets treated as new on the Server
          this.mInstance.id = null;
          this.mInstance.uuid = null;
          if (this.mInstance.data != null) {
            this.mInstance.data.forEach(d => d.id = null);
          }

          //Refresh the Availabe Types, Reload the Instance Data and Show the Dialog to start editing
          this.refreshTypesReloadDataAndShowDialog();
        },
        error => this.handleError(error)
      );
    }
  }

  deleteSelectedTagInstancesClicked(): void {
    if (this.isAnyTagInstancesSelected()) {
      this.mDeleteFlag = 2; //2 = Tag Instances
      this.mConfirmDeleteDialog.show("tag", this.getSelectedTagInstanceNames());
    }
  }

  editTagInstance(assetInstance: TagInstance): void {
    this
      .mTagService
      .getTagInstanceById(assetInstance.id)
      .then(
        response => {
          //Set the Instance being modeled in the Entity Instance Dialog to the Instance that was returned
          this.mInstance = response;

          //Set the Instance Name which is displayed in the Dialog Header
          this.mInstanceName = response.name;

          // Set the original Instance Data being displayed by the EntityInstanceDialogComponent
          this.mEntityInstanceDialogComponent.setOriginalInstanceData(this.mInstance.data);

          //Refresh the Availabe Types, Reload the Instance Data and Show the Dialog to start editing
          this.refreshTypesReloadDataAndShowDialog();
        },
        error => this.handleError(error)
      );
  }

  async refreshTypesReloadDataAndShowDialog(): Promise<void> {
    //Create a List of the available Tags that the Instance can be a type of from the List already obtained
  //   this.mAvailableEntityTypes = await this.mTagService.getAllTagDefinitions();
   if(this.mAvailableEntityTypes == null || this.mAvailableEntityTypes.length == 0) {
      console.log("Refreshing available entity types...");
      this.mAvailableEntityTypes = await this.mTagService.getAllTagDefinitions();
   }
    //Find/Set the Entity Type the Instance is the type of
    this.mInstanceEntityType = this.mInstance.asset == null? null : this.mAvailableEntityTypes.find(entityType => entityType.id == this.mInstance.asset.id);
  
    //this.loadEntityTypeUntilMatch().then(() => {
    if (this.mInstanceEntityType != null) {
      this.mInstanceEntityType.functions = this.mInstance.asset.functions;
      this.mInstanceEntityType.properties = this.mInstance.asset.properties;
    }
  //});
 
    // Need to detect the changes here so that the "mInstanceEntityType" reference update gets passed to the EntityInstanceDialogComponent being shown below
    this.cdr.detectChanges();

    //Refresh the Instance Data, loading the existing values in for the Instance being edited
    this.mEntityInstanceDialogComponent.refreshData();

    //Show the Entity Instance Dialog
    this.mEntityInstanceDialogComponent.showDialog();
  }

// async loadEntityTypeUntilMatch(): Promise<void> {
//   const pageSize = 100;
//   let start = 0;
//   let found = false;
//   const order = 'ASC';
//   const filterString = ''; 

//   this.mAvailableEntityTypes = []; 

//   const assetId = this.mInstance.asset?.id;
//   // if (!assetId) {
//   //   console.warn("No asset id found on instance.");
//   //   return;
//   // }

//   while (!found) {
//     const pageResults = await this.mTagService.getTagDefinitionsPage(start, pageSize, order, filterString);

//     if (!pageResults || !Array.isArray(pageResults.data)) {
//       console.warn("No valid tag definitions found.");
//       break;
//     }

//     // Cache loaded types
//     this.mAvailableEntityTypes.push(...pageResults.data);

//     // Check for match
//     const match = pageResults.data.find(entityType => entityType.id === assetId);
//     if (match) {
//       this.mInstanceEntityType = match;
//       console.log("✅ Found entity type match:", match);
//       found = true;
//       break;
//     }

//     start += pageSize;

//     // Stop condition
//     if (pageResults.data.length < pageSize) {
//       break; // no more pages
//     }
//   }

//   if (!found) {
//     console.warn(`⚠️ No entity type with id ${assetId} found after pagination.`);
//   }
// }



  createOrUpdateEntityInstance(): void {
    //Set the values to the Tag Instance to be created
    this.mInstance.asset = this.mInstanceEntityType;

    if (this.mInstance.id == null) {
      //ID is null, Tag Instance does not exist!  Create it...
      this
        .mTagService
        .createTagInstance(this.mInstance)
        .then(
          response => {
            this.refreshTagInstances();
            this.mEntityInstanceDialogComponent.hideDialog();
          },
          error => this.handleError(error)
        );
    } else {
      //ID is not null, Tag Instance exists!  Update it...
      this
        .mTagService
        .updateTagInstance(this.mInstance)
        .then(
          response => {
            this.refreshTagInstances();
            this.mEntityInstanceDialogComponent.hideDialog();
          },
          error => this.handleError(error)
        );
    }
  }

  selectedEntityTypeChanged(entityType: TagDefinition): void {
    this.mInstanceEntityType = entityType;
    if (this.mInstanceEntityType != null && this.mInstanceEntityType.properties == null) {
      this
        .mTagService
        .getTagDefinitionById(this.mInstanceEntityType.id)
        .then(
          foundEntityType => {
            this.mInstanceEntityType.functions = foundEntityType.functions;
            this.mInstanceEntityType.properties = foundEntityType.properties;
            this.mEntityInstanceDialogComponent.refreshData();
          },
          error => this.handleError(error)
        );
    } else {
      this.mEntityInstanceDialogComponent.refreshData();
    }
  }

  refreshInstanceData(): void {
    this
      .mTagService
      .getTagInstanceById(this.mInstance.id)
      .then(
        response => {
          this.mInstance.data = response.data;
          this.mEntityInstanceDialogComponent.setOriginalInstanceData(response.data);
          this.mEntityInstanceDialogComponent.refreshData();
        },
        error => this.handleError(error)
      );
  }

  deleteConfirmed(): void {
    if (this.mDeleteFlag == 1) {  //1 = Tag Definitions
      this
        .mTagService
        .deleteTagDefinitions(this.getSelectedTagDefinitionIds())
        .then(
          response => {
            this.refreshTagDefinitions();
          },
          error => this.handleError(error)
        );
    } else if (this.mDeleteFlag == 2) { //2 = Tag Instances
      this
        .mTagService
        .deleteTagInstances(this.getSelectedTagInstanceIds())
        .then(
          response => {
            this.refreshTagInstances();
          },
          error => this.handleError(error)
        );
    }


  }

  async refreshTagDefinitions(): Promise<void> {
    this.mTagDefinitions = [];
    this.mTagDefinitionsSelected = [];
    
    this.spinner.show();

    try {
      const responses = await this.mTagService.getAllTagsForPaging((this.currentPageTag - 1) * this.selectedPageSizeValueForTypes, this.selectedPageSizeValueForTypes, "ASC", this.selectedFilterOptionForTagDefinition.apiValue);

      console.warn("received response");
      for (const response of responses.data) {
        this.mTagDefinitions.push(response);
      }
      
      this.numberTags = responses.size;
      this.numberTagPages = (this.numberTags / this.selectedPageSizeValueForTypes);

      this.spinner.hide();
    } catch(error) {
      this.spinner.hide();
      console.log("Error calling getAllTagsForPaging");
    }
  }

  refreshTagInstances(): void {

    this.mTagInstances = [];
    this.mTagInstancesSelected = [];

    this.spinner.show();

    this.mTagService.getAllTagInstancesForPagingWithFilters((this.currentPageTagInstance - 1) * this.selectedPageSizeValueForInstances, this.selectedPageSizeValueForInstances, "ASC", this.activeFiltersForTagInstance).then(
      responses => {
        for(const response of responses.data) {
          this.mTagInstances.push(response);
        }

        this.numberTagInstances = responses.size;
        this.numberTagInstancePages = (this.numberTagInstances / this.selectedPageSizeValueForInstances);

        this.spinner.hide();
      },
      error => {
        this.spinner.hide();
        console.log("Error calling getAllTagInstancesForPaging");
      }
    );
  }

  // Called by <app-filter-bar> whenever the active filter or its value changes.
  onTagDefinitionFilterChanged(option: FilterOption): void {
    this.selectedFilterOptionForTagDefinition = option;
    this.filterStringChangedTag(1);
  }

  // Called by <app-filter-bar> whenever the active filters or a value change.
  // The active filter list is two-way bound via [(active)], so we just re-query.
  onTagInstanceFilterChanged(option: FilterOption): void {
    this.filterStringChangedTagInstance(1);
  }


  filterStringChangedTag(pageNumber?: number): void {
    if (!pageNumber) { pageNumber = 1; }
    // Show only size on this new page
    console.log("page Changed to " + pageNumber);
    this.currentPageTag = pageNumber;

    this.refreshTagDefinitions();
  }

  filterStringChangedTagInstance(pageNumber?: number): void {
    if (!pageNumber) { pageNumber = 1; }
    // Show only size on this new page
    console.log("page Changed to " + pageNumber);
    this.currentPageTagInstance = pageNumber;

    this.refreshTagInstances();
  }


  paginatorPageChangedTag(pageNumber: number): void {
    this.filterStringChangedTag(pageNumber);
  }

  paginatorPageChangedTagInstance(pageNumber: number): void {
    this.filterStringChangedTagInstance(pageNumber);
  }

  // sortTag(columnName: string): void {
  //   this.sortColumn = columnName;

  //   console.log("sortTag called for column " + columnName);
  //   console.log("sortOrder is " + this.sortOrder[columnName]);
  //   if (this.sortOrder[columnName] == "ASC") {
  //       console.log("changing to DESC");
  //       this.sortOrder[columnName] = "DESC";
  //   } else {
  //       console.log("changing to ASC");
  //       this.sortOrder[columnName] = "ASC";
  //   }

  //   this.refreshTagDefinitions();
  // }
  // sortTagInstance(columnName: string): void {
  //   this.sortColumn = columnName;

  //   console.log("sortTagInstance called for column " + columnName);

  //   if (this.sortOrder['instance_' + columnName] == "ASC") {
  //       this.sortOrder['instance_' + columnName] = "DESC";
  //   } else {
  //     this.sortOrder['instance_' + columnName] = "ASC";
  //   }

  //   this.refreshTagInstances();
  // }

  uploadTypeFileClicked(): void {
    this.mTypeFileUploadInput.nativeElement.click();
  }

  onTypeFileUploadChange(event: any): void {
    if (event.target.files.length === 1) {
      this.uploadTypes(event.target.files[0]);
    }
  }

  uploadTypes(file: any): void {
    const formData = new FormData();
    formData.append("file", file);

    this.mTagService.uploadTypes(formData).then(
        response => {
          console.log('Success');
        },
        error => {
          console.error(error);
        }
    );
  }

  downloadTypes(): void {
    this.mTagService.downloadTypes().then(
        response => {
           const blob = new Blob([response], { type: 'text/csv' });
           const url = window.URL.createObjectURL(blob);
           const a = document.createElement("a");

           document.body.appendChild(a);
//           a.style = 'diplay: none';
           a.href = url;
           a.download = 'tagtypes.csv';
           a.click();
           window.URL.revokeObjectURL(url);
        },
        error => {
          console.error(error);
        }
    );
  }



  uploadTagFileClicked(): void {
    this.mTagFileUploadInput.nativeElement.click();
  }

  onTagFileUploadChange(event: any): void {
    if (event.target.files.length === 1) {
      this.uploadTags(event.target.files[0]);
    }
  }

  uploadTags(file: any): void {
    const formData = new FormData();
    formData.append("file", file);

    this.mTagService.uploadTags(formData).then(
        response => {
          console.log('Success');
        },
        error => {
          console.error(error);
        }
    );
  }

  downloadTags(): void {
    console.log(this.mTagDefinitionsSelected[0].id);
    this.mTagService.downloadTags(this.mTagDefinitionsSelected[0].id).then(
        response => {
           const blob = new Blob([response], { type: 'text/csv' });
           const url = window.URL.createObjectURL(blob);
           const a = document.createElement("a");

           document.body.appendChild(a);
//           a.style = 'diplay: none';
           a.href = url;
           a.download = 'tags.csv';
           a.click();
           window.URL.revokeObjectURL(url);
        },
        error => {
          console.error(error);
        }
    );
  }

  handleError(error: any): void {
    /* New handling (TJB 04/18/2023) */
    if (error?.error != null) {
      this.simpleSuccessErrorDialogComponent.showErrorDialog(ERROR_DIALOG_TITLE, error.error);
    } 
    
    /* Default back to old handling */
    else if (error != null) {
      const response: Response = error;
      alert(response.text());
    }
  }

       /******************** RESULTS PER PAGE FUNCTIONS  *****************/
  
       manageResultPerPageClickedForTypes(value) {
        this.selectedPageSizeValueForTypes = value;
        this.currentPageTag = 1;
        this.refreshTagDefinitions();
      }
    
      manageResultPerPageClickedForInstances(value) {
        this.selectedPageSizeValueForInstances = value;
        this.currentPageTagInstance = 1;
        this.refreshTagInstances();
      }

  /*  
*  Handling the Right click event only on tags Instance Name Column
*/
  fetchFunctionList(event: MouseEvent, tagInstance: TagInstance): void {
    // Stop the default handling of right click event
    event.preventDefault();
    this.showContextMenu = true;
    this.showGeneralDialog = false;

    // Set the x & y co-ordinates for the context menu to open up
    this.contextMenuPosition.x = event.clientX;
    this.contextMenuPosition.y = event.clientY;

    // Empty the function List if any element still present
    this.functionList = []

    // Send the request to get the Tags Instance details using the ID
    this
      .mTagService
      .getTagInstanceById(tagInstance.id)
      .then(
        response => {
          //Set the Instance being modeled in the drop-down to the Instance that was returned
          this.mInstance = response;

          // Create the function List for the context menu from the response received 
          try {
            if (response.data && Array.isArray(response.asset.functions) && (response.asset.functions).length > 0) {
              this.functionList = response.asset.functions;

            } else {
              // Input a dummy

              this.functionList = INFRA_DUMMY_FUNCTION;
            }
          }
          catch (error) {
            this.handleError(error);
          }
        },
        error => this.handleError(error)
      );

  }

  onItemClicked(item: any) {
    // Handle the click in the dropdown menu item here 
    this.selectedFunction = item;

    if (item.id != -1) {
      // Display the Dialog Box for confirmation
      this.showGeneralDialog = true;
    }

    // Hide the Context Menu/Dropdown
    this.showContextMenu = false;

  }

}

